import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon, 
  PhoneIcon, 
  PhoneXMarkIcon,
  ClockIcon,
  CalendarIcon,
  UserIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
  XCircleIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import { Call } from '../types/Call';
import { formatCallDate } from '../services/callApi';
import { useCallManagement } from '../contexts/CallManagementContext';

interface LiveCallModalProps {
  call: Call;
  onEndCall: (callId: number, summary: string, callPickedUp: boolean, durationSeconds?: number) => void;
  onReschedule: (callId: number, newDate: string, reason: string) => void;
  onCancelCall: (callId: number, reason: string) => void;
}

const LiveCallModal: React.FC<LiveCallModalProps> = ({ call, onEndCall, onReschedule, onCancelCall }) => {
  const { minimizeCall, showLiveCallModal } = useCallManagement();
  const [duration, setDuration] = useState(0);
  const [summary, setSummary] = useState('');
  const [showEndOptions, setShowEndOptions] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [callPickedUp, setCallPickedUp] = useState<boolean | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Start timer when component mounts or when modal becomes visible
  useEffect(() => {
    if (!showLiveCallModal) return;

    // Always use the call's actual start time if available, otherwise use current time
    const startTime = call.actualStartTime ? new Date(call.actualStartTime) : new Date();
    
    console.log('LiveCallModal timer starting:', { 
      actualStartTime: call.actualStartTime, 
      startTime: startTime.toISOString(),
      callId: call.id,
      currentTime: new Date().toISOString(),
      showLiveCallModal
    });
    
    const updateTimer = () => {
      const now = new Date();
      const elapsed = Math.floor((now.getTime() - startTime.getTime()) / 1000);
      setDuration(elapsed);
    };

    // Update immediately to show current duration
    updateTimer();
    
    // Update every second
    intervalRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [call.actualStartTime, showLiveCallModal]); // Only depend on actualStartTime and visibility

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  const handleEndCallClick = () => {
    // Stop the timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    setShowEndOptions(true);
  };

  const handleCallPickedUp = () => {
    setCallPickedUp(true);
    setShowEndOptions(false);
    setShowSummaryModal(true);
  };

  const handleCallNotPickedUp = () => {
    setCallPickedUp(false);
    setShowEndOptions(false);
    setShowRescheduleModal(true);
  };

  const handleSummarySubmit = () => {
    if (!summary.trim()) {
      return;
    }
    onEndCall(call.id, summary, callPickedUp!, duration);
  };

  const handleReschedule = () => {
    if (!rescheduleDate || !rescheduleTime || !rescheduleReason.trim()) {
      return;
    }

    const newDateTime = new Date(`${rescheduleDate}T${rescheduleTime}`);
    onReschedule(call.id, newDateTime.toISOString(), rescheduleReason);
    setShowRescheduleModal(false);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
  };

  const handleCancelCall = () => {
    if (!rescheduleReason.trim()) {
      return;
    }
    onCancelCall(call.id, rescheduleReason);
    setShowRescheduleModal(false);
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleReason('');
  };

  // Don't render anything if modal should be hidden
  if (!showLiveCallModal) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3 animate-pulse"></div>
                <h3 className="text-lg font-medium text-gray-900">Live Call in Progress</h3>
              </div>
              <button
                onClick={minimizeCall}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Minimize call"
              >
                <MinusIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Call Information */}
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <UserIcon className="w-5 h-5 text-gray-400 mr-3" />
                  <div>
                    <div className="text-lg font-medium text-gray-900">
                      {call.leadFirstName} {call.leadLastName}
                    </div>
                    {call.leadCompany && (
                      <div className="text-sm text-gray-500 flex items-center">
                        <BuildingOfficeIcon className="w-4 h-4 mr-1" />
                        {call.leadCompany}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">Calling</div>
                  <div className="text-lg font-medium text-gray-900">{call.phoneNumber}</div>
                </div>
              </div>

              {/* Motive/Agenda */}
              <div className="mb-4">
                <label className="text-sm font-medium text-gray-700">Motive/Agenda</label>
                <p className="text-sm text-gray-900 bg-white p-3 rounded-md mt-1">
                  {call.motiveAgenda}
                </p>
              </div>
            </div>

            {/* Timer */}
            <div className="text-center mb-8">
              <div className="text-6xl font-mono font-bold text-blue-600 mb-2">
                {formatDuration(duration)}
              </div>
              <div className="text-sm text-gray-500">Call Duration</div>
            </div>

            {/* Actions */}
            <div className="flex justify-center">
              <button
                onClick={handleEndCallClick}
                className="flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <PhoneXMarkIcon className="w-6 h-6 mr-3" />
                End Call
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* End Call Options Modal */}
      
      {showEndOptions && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">How did the call go?</h4>
              
              <div className="mb-4">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold text-blue-600 mb-2">
                    {formatDuration(duration)}
                  </div>
                  <div className="text-sm text-gray-500">Call Duration</div>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={handleCallPickedUp}
                  className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-green-700 bg-green-100 border border-green-300 rounded-lg hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <CheckCircleIcon className="w-5 h-5 mr-2" />
                  Call Picked Up
                </button>
                
                <button
                  onClick={handleCallNotPickedUp}
                  className="w-full flex items-center justify-center px-6 py-3 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-lg hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <XCircleIcon className="w-5 h-5 mr-2" />
                  Didn't Pick Up
                </button>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowEndOptions(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal (for picked up calls) */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Call Summary</h4>
              
              <div className="mb-4">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold text-green-600 mb-2">
                    {formatDuration(duration)}
                  </div>
                  <div className="text-sm text-gray-500">Final Duration</div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Call Summary <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={4}
                  placeholder="What was discussed during the call?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSummaryModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowSummaryModal(false);
                    setShowRescheduleModal(true);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200"
                >
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Reschedule Call
                </button>
                <button
                  onClick={handleSummarySubmit}
                  disabled={!summary.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Complete Call
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule/Cancel Modal (for not picked up calls) */}
      {showRescheduleModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h4 className="text-lg font-medium text-gray-900 mb-4">
                {callPickedUp ? 'Reschedule Call' : 'Call Not Picked Up'}
              </h4>
              
              <div className="mb-4">
                <div className="text-center">
                  <div className="text-2xl font-mono font-bold text-red-600 mb-2">
                    {formatDuration(duration)}
                  </div>
                  <div className="text-sm text-gray-500">Call Duration</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Date</label>
                    <input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">New Time</label>
                    <input
                      type="time"
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                  <textarea
                    value={rescheduleReason}
                    onChange={(e) => setRescheduleReason(e.target.value)}
                    rows={3}
                    placeholder="Why didn't they pick up? (e.g., 'Did not pick up - will reschedule')"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-between space-x-3 mt-6">
                <button
                  onClick={() => setShowRescheduleModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleCancelCall}
                    disabled={!rescheduleReason.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Close
                  </button>
                  
                  <button
                    onClick={handleReschedule}
                    disabled={!rescheduleDate || !rescheduleTime || !rescheduleReason.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Schedule Call
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveCallModal;
